<template>
  <div
    class="custom-input-number"
    :class="[`custom-input-number--${size}`, className, { 'is-disabled': disabled }]"
  >
    <!-- 减少按钮 -->
    <button
      type="button"
      class="input-number-btn decrease"
      :disabled="disabled || currentValue <= min"
      @click="decrease"
    >
      <i class="iconfont icon-shouqi"></i>
    </button>

    <!-- 输入框 -->
    <input
      ref="inputRef"
      type="number"
      :value="displayValue"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      :readonly="readonly"
      class="input-number-input"
      @input="handleInput"
      @change="handleChange"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown="handleKeydown"
    />

    <!-- 增加按钮 -->
    <button
      type="button"
      class="input-number-btn increase"
      :disabled="disabled || currentValue >= max"
      @click="increase"
    >
      <i class="iconfont icon-zhankai"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: 'InputNumber',
  props: {
    modelValue: {
      type: Number,
      default: 0,
    },
    min: {
      type: Number,
      default: -Infinity,
    },
    max: {
      type: Number,
      default: Infinity,
    },
    step: {
      type: Number,
      default: 1,
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['large', 'medium', 'small'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    precision: {
      type: Number,
      default: undefined,
    },
    label: {
      type: String,
      default: '',
    },
    class: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'change', 'blur', 'focus', 'input'],
  data() {
    return {
      currentValue: 0,
      userInput: null,
    };
  },
  computed: {
    className() {
      return this.class;
    },
    displayValue() {
      if (this.userInput !== null) {
        return this.userInput;
      }
      const value = this.currentValue;
      if (this.precision !== undefined && typeof value === 'number') {
        return value.toFixed(this.precision);
      }
      return value;
    },
    numPrecision() {
      const stepPrecision = this.getPrecision(this.step);
      if (this.precision !== undefined) {
        return Math.max(stepPrecision, this.precision);
      }
      return stepPrecision;
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(value) {
        let newVal = value === undefined ? value : Number(value);
        if (newVal !== undefined) {
          if (isNaN(newVal)) {
            newVal = undefined;
          } else {
            newVal = this.toPrecision(newVal, this.numPrecision);
          }
        }
        this.currentValue = newVal;
        this.userInput = null;
      },
    },
  },
  methods: {
    getPrecision(value) {
      if (value === undefined) return 0;
      const valueString = value.toString();
      const dotPosition = valueString.indexOf('.');
      let precision = 0;
      if (dotPosition !== -1) {
        precision = valueString.length - dotPosition - 1;
      }
      return precision;
    },
    toPrecision(num, precision) {
      if (precision === undefined) precision = this.numPrecision;
      return parseFloat(Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision));
    },
    setCurrentValue(value) {
      const oldValue = this.currentValue;
      if (typeof value === 'number' && this.precision !== undefined) {
        value = this.toPrecision(value, this.precision);
      }
      if (value !== undefined && value >= this.max) value = this.max;
      if (value !== undefined && value <= this.min) value = this.min;
      if (oldValue === value) return;

      this.userInput = null;
      this.$emit('update:modelValue', value);
      this.$emit('input', value);
      this.$emit('change', value, oldValue);
      this.currentValue = value;
    },
    handleInput(event) {
      // 手动输入时只更新显示值，不触发事件
      this.userInput = event.target.value;
      const value = event.target.value === '' ? undefined : Number(event.target.value);

      // 只更新内部值用于显示，不触发事件
      if (!isNaN(value) || event.target.value === '') {
        this.currentValue = value;
        // 立即更新v-model，但不触发change和input事件
        this.$emit('update:modelValue', value);
      }
    },
    handleChange(event) {
      // change事件在输入完成后触发，但我们主要依赖blur
      this.userInput = null;
    },
    handleBlur(event) {
      // blur时才触发业务事件
      const value = this.currentValue;
      const oldValue = this.modelValue;

      // 触发所有相关事件
      this.$emit('blur', event);
      this.$emit('input', value);
      this.$emit('change', value, oldValue);

      this.userInput = null;
    },
    handleFocus(event) {
      this.$emit('focus', event);
    },
    handleKeydown(event) {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.increase();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.decrease();
      }
    },
    increase() {
      if (this.disabled || this.readonly) return;
      const value = this.currentValue || 0;
      const newVal = this.toPrecision(value + this.step, this.numPrecision);
      this.setCurrentValue(newVal);
    },
    decrease() {
      if (this.disabled || this.readonly) return;
      const value = this.currentValue || 0;
      const newVal = this.toPrecision(value - this.step, this.numPrecision);
      this.setCurrentValue(newVal);
    },
    focus() {
      this.$refs.inputRef?.focus();
    },
    blur() {
      this.$refs.inputRef?.blur();
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-input-number {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 34px;
  line-height: 34px;
  box-sizing: border-box;
  border: 1px solid #e3e1de;
  border-radius: 5px;
  font-size: 14px;
  background-color: #ffffff;
  transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);
  &:hover:not(.is-disabled) {
    border-color: #e3e1de;
  }
  &.is-disabled {
    border-color: #e3e1de;
    color: #cccccc;
    background-color: #f5f7fa;
    cursor: not-allowed;
  }

  // 不同尺寸
  &--medium {
    .input-number-btn {
      width: 34px;
      height: 34px;
    }
    .input-number-input {
      height: 34px;
      font-size: 14px;
    }
  }
  .input-number-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    font-size: 13px;
    color: #080506;
    background: transparent;
    transition: color 0.2s;
    cursor: pointer;
    i {
      font-size: 12px;
    }
    &:hover:not(:disabled) {
      color: #080506;
    }
    &:disabled {
      color: #cccccc;
      cursor: not-allowed;
    }
    &.decrease {
      border-right: 1px solid #e3e1de;
    }
    &.increase {
      border-left: 1px solid #e3e1de;
    }
  }
  .input-number-input {
    padding: 0;
    width: 44px;
    border: none;
    text-align: center;
    font-family: $f-pr;
    color: #080506;
    background: transparent;
    outline: none;
    flex: 1;
    &:disabled {
      color: #e3e1de;
      cursor: not-allowed;
    }

    // 隐藏原生数字输入框的箭头
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      margin: 0;
      appearance: none;
    }
    &[type='number'] {
      appearance: textfield;
    }
  }
  &.is-disabled {
    .input-number-btn {
      color: #cccccc;
      cursor: not-allowed;
    }
    .input-number-input {
      color: #cccccc;
      cursor: not-allowed;
    }
  }
}
</style>
